import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import BrandMark from '../components/BrandMark/BrandMark';
import ProjectIcon from '../components/ProjectIcon/ProjectIcon';
import ContactSection from '../components/site/ContactSection/ContactSection';
import Marker from '../components/ui/Marker/Marker';
import PageHero from '../components/ui/PageHero/PageHero';
import PlaceBanner from '../components/about/PlaceBanner/PlaceBanner';
import Section from '../components/ui/Section/Section';
import SectionHeader from '../components/ui/SectionHeader/SectionHeader';
import teamPhoto from '../assets/img/about/team.webp';
import vellbergPhoto from '../assets/img/about/vellberg.webp';
import { history } from '../data/history';
import { getProject, plainProjectName } from '../data/projects';
import { services } from '../data/services';
import { usePageMeta } from '../hooks/usePageMeta';
import { useLanguage } from '../i18n/useLanguage';
import type { TitledDetail, TitledText } from '../locales/types';
import styles from './About.module.css';

const PHOTO_WIDTH = 1600;
const PHOTO_HEIGHT = 1200;
const TEAM_WIDTH = 668;
const TEAM_HEIGHT = 501;
const PHOTO_CREDIT = 'Roman Eisele, CC BY-SA 4.0';
const PHOTO_SOURCE = 'https://commons.wikimedia.org/wiki/File:Vellberg_-_St%C3%A4dtle_-_Ansicht_in_der_blauen_Stunde_3.jpg';

export default function About() {
    const { t } = useTranslation();
    const { localizedPath } = useLanguage();
    usePageMeta(t('meta.aboutTitle'), t('meta.aboutDescription'));

    const partners = t('about.who.partners', { returnObjects: true }) as unknown as string[];
    const placeFacts = t('about.who.placeFacts', { returnObjects: true }) as unknown as TitledText[];
    const principles = t('about.how.items', { returnObjects: true }) as unknown as TitledText[];

    return (
        <>
            <PageHero title={t('about.hero.title')} lead={t('about.hero.lead')} />

            <Section theme="light" shape="sheet" labelledBy="about-who-title">
                <div className={`page-inner ${styles.who}`}>
                    <div className={styles.whoText}>
                        <BrandMark className={styles.logo} />
                        <h2 id="about-who-title" className={styles.title}>{t('about.who.title')}</h2>
                        <p className={styles.nameOrigin}>
                            <Trans i18nKey="about.who.name" components={{ mark: <Marker /> }} />
                        </p>
                        <ul className={styles.partners}>
                            {partners.map((partner) => (
                                <li key={partner} className={styles.partner}>
                                    <span className={styles.partnerName}>{partner}</span>
                                    <span className={styles.partnerRole}>{t('about.who.role')}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <figure className={styles.team}>
                        <img src={teamPhoto} alt={t('about.who.teamAlt')} width={TEAM_WIDTH} height={TEAM_HEIGHT} loading="lazy" decoding="async" className={styles.teamImage} />
                        <figcaption className={styles.placeCaption}>{t('about.who.teamCaption')}</figcaption>
                    </figure>
                </div>
                <PlaceBanner
                    image={vellbergPhoto}
                    width={PHOTO_WIDTH}
                    height={PHOTO_HEIGHT}
                    alt={t('about.who.photoAlt')}
                    eyebrow={t('about.who.placeEyebrow')}
                    title={t('about.who.placeTitle')}
                    caption={t('about.who.place')}
                    facts={placeFacts}
                    credit={PHOTO_CREDIT}
                    creditHref={PHOTO_SOURCE}
                />
            </Section>

            <Section theme="dark" shape="sheet" labelledBy="about-what-title">
                <div className="page-inner">
                    <SectionHeader id="about-what-title" title={t('home.services.title')} lead={t('about.what.lead')} />
                    <ul className={styles.blocks}>
                        {services.map((service) => {
                            const copy = t(`home.services.items.${service.id}`, { returnObjects: true }) as unknown as TitledDetail;
                            return (
                                <li key={service.id} className={styles.block}>
                                    <h3 className={styles.blockTitle}>{copy.title}</h3>
                                    <p>{copy.detail}</p>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </Section>

            <Section theme="light" shape="sheet" labelledBy="about-how-title">
                <div className="page-inner">
                    <SectionHeader id="about-how-title" title={t('about.how.title')} />
                    <ul className={styles.blocks}>
                        {principles.map((principle) => (
                            <li key={principle.title} className={styles.block}>
                                <h3 className={styles.blockTitle}>{principle.title}</h3>
                                <p>{principle.body}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            </Section>

            <Section theme="dark" shape="sheet" labelledBy="about-history-title">
                <div className="page-inner">
                    <SectionHeader id="about-history-title" title={t('about.history.title')} lead={t('about.history.lead')} />
                    <ol className={styles.history}>
                        {history.map((entry) => (
                            <li key={entry.year} className={styles.era}>
                                <span className={styles.year}>{entry.year}</span>
                                <div className={styles.eraBody}>
                                    <p className={styles.eraText}>{t(`about.history.items.${entry.year}`)}</p>
                                    <ul className={styles.eraProjects}>
                                        {entry.projects.map((slug) => {
                                            const project = getProject(slug);
                                            return (
                                                <li key={slug}>
                                                    <Link to={localizedPath(`/work/${slug}`)} className={styles.eraProject} aria-label={t('project.open', { name: plainProjectName(project) })}>
                                                        <ProjectIcon project={project} size="medium" />
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </Section>

            <ContactSection />
        </>
    );
}
